/**
 * Re-encode user images in the browser to drop EXIF/GPS before S3 upload.
 * Server-side Lambda sanitization remains the authoritative control.
 */
const IMAGE_MIME_TYPES = new Set(['image/jpeg', 'image/jpg', 'image/png']);

function outputMimeType(file: File): string {
  return file.type === 'image/png' ? 'image/png' : 'image/jpeg';
}

function outputExtension(mime: string): string {
  return mime === 'image/png' ? '.png' : '.jpg';
}

function canvasToBlob(canvas: HTMLCanvasElement, mime: string): Promise<Blob | null> {
  return new Promise((resolve) => {
    canvas.toBlob(resolve, mime, mime === 'image/jpeg' ? 0.92 : undefined);
  });
}

export async function stripImageMetadata(file: File): Promise<File> {
  const mime = file.type.toLowerCase();
  if (!IMAGE_MIME_TYPES.has(mime)) {
    return file;
  }

  let bitmap: ImageBitmap | null = null;
  try {
    bitmap = await createImageBitmap(file);
    const canvas = document.createElement('canvas');
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;

    const context = canvas.getContext('2d');
    if (!context) {
      return file;
    }

    context.drawImage(bitmap, 0, 0);

    const outputMime = outputMimeType(file);
    const blob = await canvasToBlob(canvas, outputMime);
    if (!blob) {
      return file;
    }

    const baseName = file.name.replace(/\.[^.]+$/, '') || 'image';
    return new File([blob], `${baseName}${outputExtension(outputMime)}`, {
      type: outputMime,
      lastModified: Date.now(),
    });
  } catch {
    return file;
  } finally {
    bitmap?.close();
  }
}

export function isUserImageFile(file: File): boolean {
  return IMAGE_MIME_TYPES.has(file.type.toLowerCase());
}
